import { Suspense } from 'react';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { Loader } from '@givebutter/primitives/loader';
import { Integrations } from 'assets/js/pages/account-settings/account-integrations/Integrations';
import { QuickBooksConfigure } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksConfigure';
import { QuickBooksConnect } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksConnect';
import { QuickBooksIndex } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksIndex';
import { QuickBooksRuleDrawer } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/QuickBooksRuleDrawer';
import { WidgetsRouter } from 'assets/js/pages/account-settings/account-integrations/WidgetsRouter';
import { Zapier } from 'assets/js/pages/account-settings/account-integrations/Zapier';
import { APIKeySettings } from 'assets/js/pages/account-settings/account-integrations/api-key-settings/APIKeySettings';
import { EditAPIKeyDrawer } from 'assets/js/pages/account-settings/account-integrations/api-key-settings/EditAPIKeyDrawer';
import { NewAPIKeyDrawer } from 'assets/js/pages/account-settings/account-integrations/api-key-settings/NewAPIKeyDrawer';
import { WebhookDrawer } from 'assets/js/pages/account-settings/account-integrations/webhooks/WebhookDrawer';
import { Webhooks } from 'assets/js/pages/account-settings/account-integrations/webhooks/Webhooks';
import { TransactionDrawerRouter } from 'assets/js/pages/transactions/TransactionDrawerRouter';
import { DrawerRoute } from 'components/drawer-route';
import * as NavMenu from 'components/nav-menu';
import { Route } from 'components/router/Route';
import { TestIds } from 'constants/tests';
import { useFlags } from 'launchdarkly-react-client-sdk';

const IntegrationsRoutes = {
    Webhooks: 'webhooks',
    Widgets: 'widgets',
    Api: 'api',
    Zapier: 'zapier',
    QuickBooks: 'quickbooks',
};

const IntegrationsRouter = () => {
    const match = useRouteMatch();
    const { elements: showElements = false, quickBooksIntegration = false } = useFlags();

    return (
        <div className="grid grid-cols-12 gap-4">
            <NavMenu.Root
                className="col-span-full lg:col-span-3"
                orientation="vertical"
                aria-label="Integrations"
            >
                <NavMenu.Item exact={true} to={match.url}>
                    Third Party
                </NavMenu.Item>
                <NavMenu.Item
                    to={`${match.url}/${IntegrationsRoutes.Zapier}`}
                    data-testid={TestIds.AccountSettings.Integrations.Zapier}
                >
                    <img
                        alt="Zapier"
                        className="h-[24px] max-w-[64px]"
                        src={`/images/logos/zapier.svg`}
                    />
                    <p className="sr-only">Zapier</p>
                </NavMenu.Item>
                {quickBooksIntegration && (
                    <NavMenu.Item
                        to={`${match.url}/${IntegrationsRoutes.QuickBooks}`}
                        data-testid={TestIds.AccountSettings.Integrations.QuickBooks}
                    >
                        <img alt="QuickBooks" src={`/images/logos/quickbooks_combologo.svg`} />
                    </NavMenu.Item>
                )}
                <NavMenu.Item
                    to={`${match.url}/${IntegrationsRoutes.Webhooks}`}
                    data-testid={TestIds.AccountSettings.Integrations.Webhooks}
                >
                    Webhooks
                </NavMenu.Item>
                {showElements ? (
                    <NavMenu.Item
                        to={`${match.url}/${IntegrationsRoutes.Widgets}`}
                        data-testid={TestIds.AccountSettings.Integrations.Widgets}
                    >
                        Widgets
                    </NavMenu.Item>
                ) : null}
                <NavMenu.Item
                    to={`${match.url}/${IntegrationsRoutes.Api}`}
                    data-testid={TestIds.AccountSettings.Integrations.Api}
                >
                    API
                </NavMenu.Item>
            </NavMenu.Root>

            <div className="col-span-full lg:col-span-9">
                <Suspense fallback={<Loader />}>
                    <Switch>
                        <Route exact path={match.path} component={Integrations} />
                        <Route
                            path={`${match.path}/${IntegrationsRoutes.Zapier}`}
                            component={Zapier}
                        />
                        {quickBooksIntegration && (
                            <Route path={`${match.path}/quickbooks`} component={QuickBooksRouter} />
                        )}
                        <Route
                            path={`${match.path}/${IntegrationsRoutes.Webhooks}`}
                            component={WebhooksRouter}
                        />
                        {showElements ? (
                            <Route
                                path={`${match.path}/${IntegrationsRoutes.Widgets}`}
                                component={WidgetsRouter}
                            />
                        ) : null}
                        <Route
                            path={`${match.path}/${IntegrationsRoutes.Api}`}
                            component={APIKeysRouter}
                        />
                        <Route>
                            <Redirect to={match.url} />
                        </Route>
                    </Switch>
                </Suspense>
            </div>
        </div>
    );
};

const APIKeysRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/new`} component={NewAPIKeyDrawer} />
            <DrawerRoute path={`${match.path}/:apiKeyId/edit`} component={EditAPIKeyDrawer} />
            <Switch>
                <Route path={match.path} component={APIKeySettings} />
            </Switch>
        </>
    );
};

const WebhooksRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute
                path={[`${match.path}/new`, `${match.path}/:webhookId/edit`]}
                component={WebhookDrawer}
            />
            <Switch>
                <Route path={match.path} component={Webhooks} />
            </Switch>
        </>
    );
};

const QuickBooksRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <Switch>
                <Route
                    exact
                    path={[match.path, `${match.path}/transaction/:transaction_id(\\d+)`]}
                    component={QuickBooksIndex}
                />
                <Route exact path={`${match.path}/connect`} component={QuickBooksConnect} />
                <Route path={`${match.path}/configure`} component={QuickBooksConfigureRouter} />
            </Switch>
            <DrawerRoute
                path={`${match.path}/transaction/:transaction_id(\\d+)`}
                component={TransactionDrawerRouter}
            />
        </>
    );
};

const QuickBooksConfigureRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute
                path={[`${match.path}/rules/new`, `${match.path}/rules/:ruleId/edit`]}
                component={QuickBooksRuleDrawer}
            />
            <Switch>
                <Route path={match.path} component={QuickBooksConfigure} />
            </Switch>
        </>
    );
};

export { IntegrationsRouter };
